import cn from 'classnames';
import { FC } from 'react';
import { AiOutlineDelete, AiOutlineEdit } from 'react-icons/ai';
import { TbRestore } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import { ActionsIcon } from '../../../../../../assets/svg/actions';
import { QrIcon } from '../../../../../../assets/svg/qr';
import { Chip } from '../../../../../../components/chip/chip';
import { IconButton } from '../../../../../../components/icon-button/icon-button';
import { InfiniteScroll } from '../../../../../../components/infinite-scroll/infinite-scroll';
import { Label } from '../../../../../../components/label/label';
import { HasAccess } from '../../../../../../features/has-access/has-access';
import { Tooltip } from '../../../../../../features/tooltip/tooltip';
import { formatRuDateString } from '../../../../../../helpers/date-time';
import { buildParams, concatPath } from '../../../../../../helpers/path';
import { serviceMap } from '../../../../../../helpers/services';
import { useLang } from '../../../../../../hooks/use-lang';
import { useModalParams } from '../../../../../../hooks/use-modal-params';
import { useQueryParams } from '../../../../../../hooks/use-query-params';
import { ManagerPath, QrCodesPath, RootPath } from '../../../../../../routes/manager/manager-enums';
import { useBranchesStore } from '../../../../../../store/use-branch-store';
import { useQrStore } from '../../../../../../store/use-qr-store';
import s from './qr-cards.module.css';

interface Props {
  onEnd: () => void;
}

export const QrCards: FC<Props> = ({ onEnd }) => {
  const { staticList, isLoadingFetch } = useQrStore();
  const { t, getLocale } = useLang();
  const { place } = useBranchesStore();
  const { addQueryParams } = useQueryParams();

  const { handleOpenModal: handleOpenQrModal } = useModalParams('viewStaticQr');

  if (!staticList?.length && !isLoadingFetch) {
    return <div>{t('Здесь пока нет QR кодов')}</div>;
  }

  return (
    <ul className={s.feature}>
      {staticList?.map((item) => (
        <li key={item.id} className={cn(s.item, item.is_deleted && s.disabled)}>
          <div className={s.header}>
            <div className={s.view}>
              <IconButton
                className={s.viewButton}
                onClick={() =>
                  handleOpenQrModal({
                    qrId: item.id,
                    qrName: getLocale(item.name),
                  })
                }
              >
                <QrIcon />
              </IconButton>
              <div className={s.base}>
                {t('Создано')}
                <span className={s.date}>{formatRuDateString(item.created_at)}</span>
              </div>
            </div>
            <HasAccess service="qr" permissions={['write']}>
              {item.is_deleted ? (
                <IconButton
                  tooltip={t('Восстановить')}
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    addQueryParams({
                      restoreId: String(item.id),
                      restoreName: getLocale(item.name),
                    })
                  }
                >
                  <TbRestore />
                </IconButton>
              ) : (
                <Tooltip
                  triggerWrapperClassName={s.actionsButton}
                  optionsWrapperClassName={s.actions}
                  trigger={<ActionsIcon />}
                  offset={12}
                >
                  <div className={s.actions}>
                    <Link
                      to={
                        concatPath([
                          RootPath.Manager,
                          ManagerPath.QrCodes,
                          QrCodesPath.EditStatic,
                        ]) + buildParams({ id: item.id })
                      }
                    >
                      <IconButton tooltip={t('Редактировать')} size="sm" variant="secondary">
                        <AiOutlineEdit />
                      </IconButton>
                    </Link>
                    <IconButton
                      tooltip={t('Удалить')}
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        addQueryParams({
                          deleteId: item.id,
                          deleteName: getLocale(item.name),
                        })
                      }
                    >
                      <AiOutlineDelete />
                    </IconButton>
                  </div>
                </Tooltip>
              )}
            </HasAccess>
          </div>
          <div className={s.content}>
            <div className={s.branch}>
              {t('Филиал')}:
              <span className={s.branchName}>
                {getLocale(place?.find((branch) => branch.id === item.place_id)?.name)}
              </span>
            </div>
            <span className={s.qrName}>{getLocale(item.name)}</span>
            <ul className={s.services}>
              {item.services?.map((service) => (
                <Chip key={service} variant="info" size="md">
                  {t(serviceMap[service])}
                </Chip>
              ))}
            </ul>
          </div>
          <div className={s.footer}>
            <Label className={s.label}>{t('Ответственные группы')}:</Label>
            {item.tags?.length ? (
              <div className={s.tags}>
                {item.tags?.length >= 1 && <span>{item.tags[0].value}</span>}
                {item.tags.length > 1 && <span>, +{item.tags.length - 1}</span>}
              </div>
            ) : (
              <span className={s.noTags}>{t('Не указано')}</span>
            )}
          </div>
        </li>
      ))}
      <InfiniteScroll onEnterView={onEnd} />
    </ul>
  );
};
